/* Importing the React library and the useCallback, useState, and useEffect hooks. */
import React, { useCallback, useMemo, useState, useEffect } from 'react';

/* Importing the useDispatch hook from the react-redux library. */
import { useDispatch } from 'react-redux';

/* Importing the prop-types and moment packages. */
import PropTypes from 'prop-types';
import moment from 'moment';

/* Importing the components from the material-ui library. */
import { Box, Grid, Paper, Typography } from '@mui/material';
import { red } from '@mui/material/colors';

/* Importing the getPatient function from the patientSlice.js file. */
import { getPatient } from '../features/patient/patientSlice';
import { searchObservation } from '../features/observation/observationSlice';

/* Importing the formatPatient function from the utils folder. */
import { fPatient } from '../utils/Patient/Patient.utils.format';

import PlayNotifSound from './PlayNotifSound';

import CTG_BG from './CTG_BG.webp';

import {
  BloodDropIcon,
  BloodPressureIcon,
  CoronaryCareUnitIcon,
  HeartCardiogramIcon,
  RespirologyIcon,
  ThermometerIcon,
} from './HealthIcons';

TelemetryCard.propTypes = {
  patientID: PropTypes.string,
  observationData: PropTypes.object,
  columnSize: PropTypes.object,
  notification: PropTypes.bool,
  notificationSound: PropTypes.string,
};

moment.updateLocale('en', {
  relativeTime: {
    mm: '%d minutes',
    h: '1 hour',
    hh: '%d hours',
  },
});

/**
 * The TelemetryCard component is used to register a device with the server.
 * It is used to register a device with the server.
 * @returns None
 */
export default function TelemetryCard(props) {
  /* Destructuring the props object and assigning the isEdit property to a variable of the same name. */
  const { columnSize, patientID, observationData, notification, notificationSound } = props;

  /* Importing the useDispatch hook from the react-redux library. */
  const dispatch = useDispatch();

  const observationTypes = useMemo(
    () => [
      {
        type: 'HR',
        icon: HeartCardiogramIcon,
        units: 'BPM',
        color: '#00FF00',
        code: '76282-3',
        upperLimit: 'heartrate_upper_bpm',
        lowerLimit: 'heartrate_lower_bpm',
        display: true,
      },
      {
        type: 'SPO2',
        icon: BloodDropIcon,
        units: '%',
        color: '#00FFFF',
        code: '59407-7',
        upperLimit: 'oxygen_upper_saturation',
        lowerLimit: 'oxygen_lower_saturation',
        display: true,
      },
      {
        type: 'RESP',
        icon: RespirologyIcon,
        units: 'RPM',
        color: '#FFFF00',
        code: '76270-8',
        upperLimit: 'respiratory_upper_rpm',
        lowerLimit: 'respiratory_lower_rpm',
        display: true,
      },
      {
        type: 'PR',
        icon: CoronaryCareUnitIcon,
        units: 'BPM',
        color: '#00FFFF',
        code: '8889-8',
        upperLimit: 'pulserate_upper_bpm',
        lowerLimit: 'pulserate_lower_bpm',
        display: true,
      },
      {
        type: 'TEMP',
        icon: ThermometerIcon,
        units: '°C',
        color: 'white',
        code: '8310-5',
        upperLimit: 'temperature_upper',
        lowerLimit: 'temperature_lower',
        display: true,
      },
      {
        type: 'NIBP',
        icon: BloodPressureIcon,
        units: 'mmHg',
        color: 'white',
        code: '85354-9',
        display: true,
      },
      {
        type: 'SYSTOLIC',
        icon: BloodPressureIcon,
        units: 'mmHg',
        color: 'white',
        code: '8480-6',
        upperLimit: 'bp_systolic_upper',
        lowerLimit: 'bp_systolic_lower',
        display: false,
      },
      {
        type: 'DIASTOLIC',
        icon: BloodPressureIcon,
        units: 'mmHg',
        color: 'white',
        code: '8462-4',
        upperLimit: 'bp_diastolic_upper',
        lowerLimit: 'bp_diastolic_lower',
        display: false,
      },
    ],
    []
  );

  /* Setting the initial state of the Patient object. */
  const [Patient, setPatient] = useState({
    name: [{ text: null }],
    birthDate: null,
    gender: null,
    'patient-pregnancyStatus': false,
  });
  const [PatientName, setPatientName] = useState('--');
  const [PatientConfig, setPatientConfig] = useState({
    heartrate_upper_bpm: 100,
    heartrate_lower_bpm: 50,
    respiratory_upper_rpm: 20,
    respiratory_lower_rpm: 12,
    pulserate_upper_bpm: 100,
    pulserate_lower_bpm: 50,
    bp_systolic_upper: 120,
    bp_systolic_lower: 90,
    bp_diastolic_upper: 80,
    bp_diastolic_lower: 60,
    temperature_upper: 42,
    temperature_lower: 35.8,
  });

  const [PatientObservations, setPatientObservations] = useState({});

  const [ObservationValues, setObservationValues] = useState({
    HR: {
      value: '--',
      effectivity: '',
      error: false,
    },
    SPO2: {
      value: '--',
      effectivity: '',
      error: false,
    },
    RESP: {
      value: '--',
      effectivity: '',
      error: false,
    },
    TEMP: {
      value: '--',
      effectivity: '',
      error: false,
    },
    PR: {
      value: '--',
      effectivity: '',
      error: false,
    },
    SYSTOLIC: {
      value: '--',
      effectivity: '',
      error: false,
    },
    DIASTOLIC: {
      value: '--',
      effectivity: '',
      error: false,
    },
  });

  const filterObservationData = useCallback(() => {
    if (observationData[`Patient/${patientID}`]) {
      setPatientObservations(observationData[`Patient/${patientID}`]);
    }
  }, [observationData, patientID]);

  const setDisplayValues = useCallback(() => {
    const newObservationValues = {
      ...ObservationValues,
    };

    observationTypes.forEach((type) => {
      if (type.type !== 'NIBP') {
        if (PatientObservations[type.code]) {
          newObservationValues[type.type] = PatientObservations[type.code];
          if (
            +PatientObservations[type.code].value > PatientConfig[type.upperLimit] ||
            +PatientObservations[type.code].value < PatientConfig[type.lowerLimit]
          ) {
            newObservationValues[type.type].error = true;
            if (notification) {
              PlayNotifSound(notificationSound);
            }
          } else {
            newObservationValues[type.type].error = false;
          }
        }
      }

      // if (PatientObservations[type.code.systolic]) {
      //   newObservationValues.NIBPSYS = PatientObservations[type.code.systolic];
      //   if (
      //     +PatientObservations[type.code.systolic].value > PatientConfig[type.upperLimit.systolic] ||
      //     +PatientObservations[type.code.systolic].value < PatientConfig[type.lowerLimit.systolic]
      //   ) {
      //     newObservationValues.NIBPSYS.error = true;
      //   } else {
      //     newObservationValues.NIBPSYS.error = false;
      //   }
      // }

      // if (PatientObservations[type.code.diastolic]) {
      //   newObservationValues.NIBPDIA = PatientObservations[type.code.diastolic];
      //   if (
      //     +PatientObservations[type.code.diastolic].value > PatientConfig[type.upperLimit.diastolic] ||
      //     +PatientObservations[type.code.diastolic].value < PatientConfig[type.lowerLimit.diastolic]
      //   ) {
      //     newObservationValues.NIBPDIA.error = true;
      //   } else {
      //     newObservationValues.NIBPDIA.error = false;
      //   }
      // }
    });

    setObservationValues(newObservationValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PatientObservations, PatientConfig]);

  const observationSearch = useCallback(
    async (searchTerm, code) => {
      const resultAction = await dispatch(searchObservation({ query: searchTerm }));
      const initialPatientObservations = {};
      if (searchObservation.fulfilled.match(resultAction)) {
        const bundle = resultAction.payload;

        if (Array.isArray(bundle.entry)) {
          if (bundle.entry.length > 0) {
            const effectivity = bundle.entry[0].resource.effectiveDateTime;

            if (!PatientObservations[code] || moment(effectivity).isAfter(PatientObservations[code].effectivity)) {
              if (code === observationTypes.find((el) => el.type === 'NIBP').code) {
                const components = bundle.entry[0].resource.component;
                const sysCode = observationTypes.find((el) => el.type === 'SYSTOLIC').code;
                const diaCode = observationTypes.find((el) => el.type === 'DIASTOLIC').code;
                const systolic = components.find((component) => component.code.coding[0].code === sysCode);
                const diastolic = components.find((component) => component.code.coding[0].code === diaCode);

                initialPatientObservations[sysCode] = {
                  value: systolic.valueQuantity.value,
                  effectivity,
                };

                initialPatientObservations[diaCode] = {
                  value: diastolic.valueQuantity.value,
                  effectivity,
                };
              } else {
                const { value } = bundle.entry[0].resource.valueQuantity;
                initialPatientObservations[code] = { value, effectivity };
              }
            }
          }
        }
      }
      return initialPatientObservations;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const getInitialPatientObservations = useCallback(() => {
    let initialPatientObservations = {
      ...PatientObservations,
    };

    observationTypes
      .filter((el) => el.display)
      .forEach(async (type) => {
        const duration = moment.utc().subtract(6, 'hours').format('YYYY-MM-DDTHH:mm:ss');
        // .subtract(1, 'seconds');
        // if (type.type !== 'NIBP') {
        const searchTerm = `subject=Patient/${patientID}&_sort=-date&_count=1&code=${type.code}&date=ge${duration}`;
        const obs = await observationSearch(searchTerm, type.code);

        initialPatientObservations = {
          ...initialPatientObservations,
          ...obs,
        };

        setPatientObservations(initialPatientObservations);
        // }

        // else {
        //   let searchTerm = `subject=Patient/${patientID}&_sort=-date&_count=1&code=${type.code.systolic}&date=ge${duration}`;
        //   const obsSys = await observationSearch(searchTerm, type.code.systolic);

        //   searchTerm = `subject=Patient/${patientID}&_sort=-date&_count=1&code=${type.code.diastolic}&date=ge${duration}`;
        //   const obsDia = await observationSearch(searchTerm, type.code.diastolic);
        //   initialPatientObservations = {
        //     ...PatientObservations,
        //     ...obsSys,
        //     ...obsDia,
        //   };
        // }
        setPatientObservations(initialPatientObservations);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observationSearch, observationTypes, patientID]);

  const setConfig = useCallback((patientObj) => {
    const config = {
      heartrate_upper_bpm: 100,
      heartrate_lower_bpm: 50,
      oxygen_upper_saturation: 100,
      oxygen_lower_saturation: 94,
      respiratory_upper_rpm: 20,
      respiratory_lower_rpm: 12,
      pulserate_upper_bpm: 100,
      pulserate_lower_bpm: 50,
      bp_systolic_upper: 120,
      bp_systolic_lower: 90,
      bp_diastolic_upper: 80,
      bp_diastolic_lower: 60,
      temperature_upper: 42,
      temperature_lower: 35.8,
    };

    Object.keys(config).forEach((key) => {
      if (patientObj[`patient-lifeline-config-${key}`]) {
        if (
          patientObj[`patient-lifeline-config-${key}`] !== '' &&
          !Number.isNaN(patientObj[`patient-lifeline-config-${key}`])
        ) {
          config[key] = +patientObj[`patient-lifeline-config-${key}`];
        }
      }
    });
    setPatientConfig(config);
  }, []);

  /**
   * Initializes the data for the page.
   */
  const initializeData = useCallback(async () => {
    if (patientID) {
      const resultAction = await dispatch(getPatient(patientID));
      if (getPatient.fulfilled.match(resultAction)) {
        const data = resultAction.payload;
        if (data) {
          const patientObj = fPatient(data);
          setPatient(patientObj);
          setConfig(patientObj);
          setPatientName(data.name[0].text || data.id);
        }
      }
      getInitialPatientObservations();
    }
  }, [dispatch, getInitialPatientObservations, patientID, setConfig]);

  /* Calling the initializeData function when the component mounts. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  useEffect(() => {
    filterObservationData();
  }, [filterObservationData, observationData]);

  useEffect(() => {
    setDisplayValues();
  }, [setDisplayValues, PatientObservations]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayValues();
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [setDisplayValues]);

  function getEffLocal(eff) {
    const isIsoWithTimeZone = moment(eff, moment.ISO_8601, true).isValid();

    let effLocal = '';
    if (isIsoWithTimeZone) {
      effLocal = moment.utc(eff).local();
    } else {
      effLocal = moment.utc(`${eff}+00:00`).local();
    }
    return effLocal;
  }

  const DisplayValue = (type) => {
    let showAgo = false;
    let howLongAgo = '';
    let valVariant = 'h1';
    let valColor = type.color;
    let val = '';

    if (type.type !== 'NIBP') {
      const obs = ObservationValues[type.type];
      val = obs.value;
      if (obs.error) {
        valColor = red[500];
      }
      if (obs.effectivity !== '') {
        const effLocal = getEffLocal(obs.effectivity);
        if (moment().diff(effLocal, 'minutes') >= 1) {
          howLongAgo = moment(effLocal).fromNow();
          showAgo = true;
          valVariant = 'h1';
        }
      }
    } else {
      // const obsSys = ObservationValues.NIBPSYS;
      // const obsDia = ObservationValues.NIBPDIA;
      const obsSys = ObservationValues.SYSTOLIC;
      const obsDia = ObservationValues.DIASTOLIC;
      if (obsSys.error || obsDia.error) {
        valColor = red[500];
      }
      if (obsSys.effectivity === obsDia.effectivity) {
        val = `${obsSys.value}/${obsDia.value}`;
        valVariant = 'h2';
        if (obsSys.effectivity !== '') {
          // const effLocal = moment.utc(`${obsSys.effectivity}+00:00`).local();
          const effLocal = getEffLocal(obsSys.effectivity);
          if (moment().diff(effLocal, 'minutes') >= 1) {
            howLongAgo = moment(effLocal).fromNow();
            showAgo = true;
            valVariant = 'h2';
          }
        }
      }
    }

    return (
      <>
        <Grid item xs={3} container direction="column" justifyContent="center" alignItems="center">
          {type.icon ? <type.icon fill={valColor} /> : ''}
        </Grid>
        <Grid item xs={6} container direction="column" justifyContent="center" alignItems="flex-end">
          <Typography variant={valVariant} color={valColor} sx={{ lineHeight: 0.8 }}>
            {val}
          </Typography>
          {showAgo && (
            <Typography variant="body2" color={valColor}>
              {howLongAgo}
            </Typography>
          )}
        </Grid>
        <Grid item xs={3} container direction="column" justifyContent="center" alignItems="flex-start">
          <Typography variant="h6" color={valColor} sx={{ pl: 1 }}>
            {type.type}
          </Typography>
          <Typography variant="body2" color={valColor} sx={{ pl: 1 }}>
            {type.units}
          </Typography>
        </Grid>
      </>
    );
  };

  return (
    <Paper
      variant="outlined"
      square
      sx={{ border: 0.5, borderColor: 'grey.500', backgroundColor: 'black', color: 'white' }}
    >
      <Grid container>
        <Grid item xs={12} container direction="column" justifyContent="center" alignItems="flex-start">
          <Typography variant="h4" sx={{ pl: 1 }}>
            {PatientName.toUpperCase()}
            {Patient.birthDate ? ` /${moment().diff(new Date(Patient.birthDate), 'years')}` : ''}
            {Patient.gender ? ` /${Patient.gender.toUpperCase()}` : ''}
          </Typography>
        </Grid>
        {observationTypes
          .filter((el) => el.display)
          .map((type, i) => (
            <Grid
              container
              item
              xs={columnSize.xs || 12}
              sm={columnSize.sm || 6}
              lg={columnSize.ls || 6}
              xl={columnSize.xl || 4}
              sx={{ border: 0.25, borderColor: 'grey.900' }}
              key={i}
            >
              {DisplayValue(type)}
            </Grid>
          ))}
        {/* Cardiotocograph placeholder */}
        {Patient['patient-pregnancyStatus'] && (
          <Grid item xs={12} container direction="column" justifyContent="center" alignItems="flex-start">
            <Box sx={{
              width: '100%',
              height: {xs: '220px', md: '300px', lg: '300px', xl: '220px'},
              backgroundImage: `url(${CTG_BG})`,
              backgroundSize: 'auto 100%',
              backgroundRepeat: 'repeat'
            }}/>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
